import { useState, useEffect } from 'react';
import {
  Leaf,
  Check,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { getProductImage } from '../../utils/imageHelper';

export function ProductTabs({
  product = {},
  details = null,
  packageItems: customPackageItems,
  onPreviewImage,
}) {
  const [activeTab, setActiveTab] = useState('overview');

  // Reset tab to 'overview' on product change
  useEffect(() => {
    setActiveTab('overview');
  }, [product?.id]);

  const packageItems = customPackageItems || details?.packageIncludes || product?.packageItems || [];
  const hasPackageItems = Boolean(product?.isPackage || packageItems.length > 0);

  // Tab definitions
  const tabs = [
    { id: 'overview', label: 'Overview', labelUrdu: 'تعارف' },
    { id: 'ingredients', label: 'Ingredients', labelUrdu: 'منتخب اجزاء' },
    { id: 'benefits', label: 'Key Benefits', labelUrdu: 'نمایاں فوائد' },
    { id: 'usage', label: 'Usage & Precautions', labelUrdu: 'طریقۂ استعمال اور احتیاط' },
  ];

  if (hasPackageItems) {
    tabs.push({ id: 'package', label: 'Course Breakdown', labelUrdu: 'کورس کی تفصیلات' });
  }

  const handlePreview = (image) => {
    if (onPreviewImage) {
      onPreviewImage(image);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-10 shadow-sm border border-gray-100/90 mb-14">
      {/* Tab Headers */}
      <div className="border-b border-gray-200 mb-8 overflow-x-auto scrollbar-hide">
        <div
          role="tablist"
          aria-label="Product details and specifications"
          className="flex space-x-2 sm:space-x-4 min-w-max pb-px"
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                id={`product-tab-${tab.id}`}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls={`product-tabpanel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3.5 px-4 sm:px-6 rounded-t-xl text-sm font-semibold transition-all border-b-2 flex items-center gap-2 focus:outline-none ${
                  isActive
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span>{tab.label}</span>
                <span className="font-urdu text-xs opacity-75" dir="rtl">
                  ({tab.labelUrdu})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Panels */}
      <div>
        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div
            id="product-tabpanel-overview"
            role="tabpanel"
            aria-labelledby="product-tab-overview"
            tabIndex={0}
            className="space-y-6 focus:outline-none"
          >
            {/* Urdu Overview */}
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 sm:p-7">
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="text-xs font-bold bg-primary text-white px-2.5 py-0.5 rounded-md">
                  اردو تعارف
                </span>
                <h3 className="font-urdu text-lg sm:text-xl font-bold text-primary" dir="rtl">
                  مصنوعات کا مکمل تعارف
                </h3>
              </div>
              <p
                className="font-urdu text-base sm:text-lg text-gray-800 leading-loose text-justify"
                dir="rtl"
              >
                {details?.overview?.ur || product.descriptionUrdu}
              </p>
            </div>

            {/* English Overview */}
            <div className="bg-gray-50/60 border border-gray-200/80 rounded-2xl p-5 sm:p-7">
              <div className="flex items-center gap-2 mb-3">
                <Leaf className="w-5 h-5 text-primary" />
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Product Overview & Herbal Formulation
                </h3>
              </div>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                {details?.overview?.en || product.description}
              </p>
            </div>
          </div>
        )}

        {/* TAB 2: INGREDIENTS */}
        {activeTab === 'ingredients' && (
          <div
            id="product-tabpanel-ingredients"
            role="tabpanel"
            aria-labelledby="product-tab-ingredients"
            tabIndex={0}
            className="space-y-8 focus:outline-none"
          >
            {/* Intro note */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-emerald-800 bg-emerald-50/70 p-3.5 rounded-xl border border-emerald-100">
              <Leaf className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>
                Formulated exclusively with standardized botanical extracts and authentic traditional herbs.
                Steroid-free and 100% natural.
              </span>
            </div>

            {/* Urdu Ingredients */}
            {details?.ingredients?.ur && details.ingredients.ur.length > 0 && (
              <div>
                <div className="flex items-center justify-end gap-2 mb-3">
                  <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
                    {details.ingredients.ur.length} قدرتی اجزاء
                  </span>
                  <h3 className="font-urdu text-lg font-bold text-gray-900" dir="rtl">
                    منتخب ہربل و نباتاتی اجزاء
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2.5 justify-end" dir="rtl">
                  {details.ingredients.ur.map((item, idx) => (
                    <span
                      key={idx}
                      className="font-urdu text-sm sm:text-base bg-emerald-50 text-emerald-950 border border-emerald-200 px-4 py-2 rounded-xl font-medium shadow-2xs hover:bg-emerald-100/70 transition"
                    >
                      🌿 {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* English Ingredients */}
            {details?.ingredients?.en && details.ingredients.en.length > 0 && (
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span>Botanical & Natural Ingredients</span>
                  </h3>
                  <span className="text-xs font-bold bg-gray-100 text-gray-700 px-2.5 py-0.5 rounded-full">
                    {details.ingredients.en.length} Botanical Items
                  </span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {details.ingredients.en.map((item, idx) => (
                    <span
                      key={idx}
                      className="text-xs sm:text-sm bg-white text-gray-800 border border-gray-200 px-3.5 py-2 rounded-xl font-medium shadow-2xs hover:border-primary/50 transition flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: KEY BENEFITS */}
        {activeTab === 'benefits' && (
          <div
            id="product-tabpanel-benefits"
            role="tabpanel"
            aria-labelledby="product-tab-benefits"
            tabIndex={0}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start focus:outline-none"
          >
            {/* English Benefits */}
            <div className="bg-gray-50/70 border border-gray-200/80 rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200/80">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-base sm:text-lg text-gray-900">
                  Key Health & Wellness Benefits
                </h3>
              </div>
              <ul className="space-y-3">
                {(details?.benefits?.en || [product.description]).map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </span>
                    <span className="leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Urdu Benefits */}
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 sm:p-6" dir="rtl">
              <div className="flex items-center justify-between gap-2 mb-4 pb-2 border-b border-emerald-200/60">
                <h3 className="font-urdu font-bold text-lg sm:text-xl text-primary">
                  نمایاں طبی اور جسمانی فوائد
                </h3>
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              </div>
              <ul className="space-y-3">
                {(details?.benefits?.ur || [product.descriptionUrdu]).map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3 font-urdu text-base sm:text-lg text-gray-800">
                    <span className="w-5 h-5 rounded-full bg-emerald-200/80 text-primary flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    </span>
                    <span className="leading-loose">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* TAB 4: USAGE & PRECAUTIONS */}
        {activeTab === 'usage' && (
          <div
            id="product-tabpanel-usage"
            role="tabpanel"
            aria-labelledby="product-tab-usage"
            tabIndex={0}
            className="space-y-6 focus:outline-none"
          >
            {/* Usage Directions Card */}
            <div className="bg-emerald-50/40 border border-emerald-200/70 rounded-2xl p-5 sm:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-base sm:text-lg text-gray-900">
                  Directions for Use / طریقۂ استعمال
                </h3>
              </div>

              <div className="space-y-4">
                {details?.usage?.ur && (
                  <div className="bg-white p-4 rounded-xl border border-emerald-100 shadow-2xs" dir="rtl">
                    <p className="font-urdu text-base sm:text-lg text-emerald-950 font-medium leading-loose">
                      {details.usage.ur}
                    </p>
                  </div>
                )}

                {details?.usage?.en && (
                  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-2xs">
                    <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                      {details.usage.en}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Precautions & Storage Card */}
            <div className="bg-amber-50/60 border border-amber-200/80 rounded-2xl p-5 sm:p-7">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-amber-200/60">
                <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <h3 className="font-bold text-base sm:text-lg text-gray-900">
                  Storage Guidelines & Precautions / احتیاطی تدابیر
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* English Precautions */}
                {details?.precautions?.en && (
                  <div>
                    <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wider mb-2">
                      Safety & Storage:
                    </h4>
                    <ul className="space-y-2">
                      {details.precautions.en.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700">
                          <span className="text-amber-500 font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Urdu Precautions */}
                {details?.precautions?.ur && (
                  <div dir="rtl">
                    <h4 className="font-urdu text-xs font-bold text-amber-900 mb-2">
                      ضروری ہدایات و نگہداشت:
                    </h4>
                    <ul className="space-y-2">
                      {details.precautions.ur.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 font-urdu text-sm text-gray-800">
                          <span className="text-amber-500 font-bold">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: PACKAGE BREAKDOWN (Only for packages) */}
        {activeTab === 'package' && hasPackageItems && (
          <div
            id="product-tabpanel-package"
            role="tabpanel"
            aria-labelledby="product-tab-package"
            tabIndex={0}
            className="space-y-8 focus:outline-none"
          >
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <span>Included in This Comprehensive 30-Day Course</span>
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                All items work synergistically to provide 360-degree nutritional herbal support for vitality, strength, and confidence.
              </p>

              {/* Product Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {packageItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50/70 rounded-2xl p-4 border border-gray-200/80 flex flex-col justify-between hover:border-primary/40 hover:bg-white hover:shadow-md transition duration-200"
                  >
                    <div>
                      <div className="aspect-video bg-white rounded-xl overflow-hidden mb-3 border border-gray-100 flex items-center justify-center p-2">
                        <img
                          src={getProductImage(item.image)}
                          alt={item.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                        <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md">
                          {item.size}
                        </span>
                      </div>
                      {item.nameUrdu && (
                        <p className="font-urdu text-primary text-xs font-semibold mb-2" dir="rtl">
                          {item.nameUrdu}
                        </p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => handlePreview(item.image)}
                      className="mt-3 w-full py-1.5 rounded-lg border border-primary/30 text-primary text-xs font-semibold hover:bg-primary hover:text-white transition"
                    >
                      Preview in Hero
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Designed To Support */}
            {details?.designedToSupport && (
              <div className="bg-emerald-50/60 border border-emerald-200/80 rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                  <span>🎯 Targeted Concerns / خاص مقاصد</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ul className="space-y-2">
                    {details.designedToSupport.en?.map((pt, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                        <span className="w-4 h-4 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center text-[10px] font-bold">
                          ✓
                        </span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>

                  <ul className="space-y-2" dir="rtl">
                    {details.designedToSupport.ur?.map((pt, idx) => (
                      <li key={idx} className="flex items-center gap-2 font-urdu text-sm text-gray-800">
                        <span className="w-4 h-4 rounded-full bg-emerald-200 text-emerald-800 flex items-center justify-center text-[10px] font-bold">
                          ✓
                        </span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Package Features Badges */}
            {details?.packageFeatures && (
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-3">Key Course Highlights:</h4>
                <div className="flex flex-wrap gap-2">
                  {details.packageFeatures.en?.map((feat, idx) => (
                    <span
                      key={idx}
                      className="px-3.5 py-1.5 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 text-xs font-semibold flex items-center gap-1.5"
                    >
                      <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                      <span>{feat}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductTabs;
